import { useCallback, useEffect, useRef, useState } from "react";
import { redirectIfPhonePe } from "../lib/payRail";
import { PayRailButtons } from "../components/payments/PayRailButtons";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Wallet, Plus, Download, Send, ShieldCheck, Loader2,
         ExternalLink, BadgeCheck,
         Landmark, Save, CheckCircle2, Clock, XCircle, AlertTriangle,
         KeyRound, Lock, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSecurityPulse } from "../hooks/useSecurityPulse";
import { WalletInductionForm, InductionPendingScreen,
         InductionRejectedScreen } from "../components/WalletInductionForm";
import { walletTier } from "../lib/walletTier";
import { LiquidBalance } from "../components/wallet/LiquidBalance";
import { BiometricPulseEntry } from "../components/wallet/BiometricPulseEntry";
import { HolographicLedgerRow } from "../components/wallet/HolographicLedgerRow";
import { WalletNetworkPulse } from "../components/wallet/WalletNetworkPulse";
import { RechargeSuccessSplash } from "../components/wallet/RechargeSuccessSplash";
import { TreasuryFlow } from "../components/wallet/TreasuryFlow";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const inr = (n) => `₹${Number(Math.round(n) || 0).toLocaleString("en-IN")}`;

/**
 * Vinci Executive Wallet · full page.
 *
 * Closed-loop digital ledger. Debits fire a fresh Security Pulse OTP.
 * Withdrawals go through the Vinci Payout Engine — no debit until
 * admin approves.
 */
export default function WalletPage() {
  const { user, api } = useAuth();
  const pulse = useSecurityPulse(api);
  const [state, setState] = useState({ balance: null, statement: [],
                                          payouts: [], bank: null,
                                          induction: null,
                                          treasury: { status: "none" },
                                          disclaimer: "", loading: true });
  const [showInductionForm, setShowInductionForm] = useState(false);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [splash, setSplash] = useState(null);        // { amount, balance_after }
  const [scanDone, setScanDone] = useState(false);   // biometric completed

  const load = useCallback(async () => {
    try {
      const [{ data: bal }, indResp, vcbResp] = await Promise.all([
        api.get("/wallet/balance"),
        api.get("/wallet/induction/status").catch(() => ({ data: null })),
        api.get("/vcb/module-status").catch(() => ({ data: { enabled: false } })),
      ]);
      const inducted = !!bal.inducted;
      let statement = [], payouts = [], bank = null, treasury = { status: "none" };
      if (inducted) {
        // Fetch the optional trio independently — a 500 on any single
        // endpoint (e.g. a legacy ledger row with a missing signature
        // field) must NOT flip the whole wallet to the LOCKED gate.
        const [stR, poR, bkR, trR] = await Promise.all([
          api.get("/wallet/statement?limit=50").catch((e) => {
            console.warn("wallet statement failed", e); return null;
          }),
          api.get("/wallet/payout/history").catch(() => null),
          api.get("/wallet/payout/linked-bank").catch(() => null),
          api.get("/wallet/treasury/status").catch(() => null),
        ]);
        statement = stR?.data?.rows || [];
        payouts = poR?.data?.rows || [];
        bank = bkR?.data?.bank || null;
        treasury = trR?.data || { status: "none" };
      }
      setState({
        balance: bal, statement, payouts, bank, treasury,
        induction: indResp?.data || null,
        vcb_enabled: !!vcbResp?.data?.enabled,
        disclaimer: bal.disclaimer,
        loading: false,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load wallet");
      setState((s) => ({ ...s, loading: false }));
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (state.loading) {
    return (
      <div className="min-h-[70vh] grid place-items-center text-[#8A8A82]">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  const bal = state.balance || {};
  const application = state.induction?.application;
  const inducted = !!bal.inducted;
  const tier = walletTier(bal.balance);
  const inductionStatus = bal.induction_status || "not_applied";
  // Gate the actual wallet UI behind acknowledgement — even when the
  // backend has already flipped `inducted=True`, the buyer must
  // "Acknowledge & Accept" the Board Resolution PDF first. That way
  // the conditional-approval workflow works end-to-end.
  const decisionAwaitingAck =
    application
    && (application.status === "approved" || application.status === "rejected")
    && !application.acknowledged;
  const acknowledgeDecision = async () => {
    try {
      await api.post("/wallet/induction/acknowledge", {});
      toast.success("Board Resolution acknowledged · PDF dispatched to your email");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Acknowledgement failed");
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Wallet is locked — render the Executive Wallet Gate
  // ─────────────────────────────────────────────────────────────
  if (!inducted || decisionAwaitingAck) {
    return (
      <div data-testid="wallet-page-locked"
           className="relative min-h-screen bg-[#050505] text-[#F5F5F0] px-6 py-12 md:px-16 overflow-hidden">
        {pulse.modal}
        <div className="pointer-events-none absolute inset-0 opacity-40"
             style={{
               background:
                 "radial-gradient(1200px 700px at 20% -10%, rgba(216,178,62,0.14), transparent 60%),"
                 + "radial-gradient(900px 600px at 90% 10%, rgba(184,115,51,0.10), transparent 60%)",
             }} />
        <div className="relative max-w-5xl mx-auto">
          {/* LOCKED WALLET CARD */}
          {!showInductionForm && inductionStatus === "not_applied" && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="relative mt-8 border border-[#D8B23E]/30 rounded-sm overflow-hidden"
              style={{
                background: "linear-gradient(135deg,rgba(216,178,62,0.08),rgba(10,9,6,0.7))",
                backdropFilter: "blur(24px)",
                boxShadow: "0 40px 120px -30px rgba(216,178,62,0.25)",
              }}
              data-testid="wallet-locked-card"
            >
              <div className="h-1 bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333]" />
              <div className="p-10 md:p-16 text-center relative">
                {/* Frosted glass balance area */}
                <div className="absolute inset-x-0 top-16 md:top-24 flex justify-center pointer-events-none opacity-25">
                  <div className="font-serif text-8xl md:text-9xl text-[#F5F5F0] blur-md select-none tracking-tighter">₹∞</div>
                </div>
                <div className="relative">
                  {/* Padlock */}
                  <div className="w-20 h-20 mx-auto rounded-full grid place-items-center border border-[#D8B23E]"
                       style={{
                         background: "linear-gradient(135deg,#1B1401,#0A0906)",
                         boxShadow: "0 0 60px rgba(216,178,62,0.4)",
                       }}>
                    <Lock className="w-8 h-8 text-[#D8B23E]" />
                  </div>
                  <div className="font-grotesk text-[10px] uppercase tracking-[0.5em] text-[#8A6A1A] mt-8">
                    Access by Application Only
                  </div>
                  <h1 className="mt-4 font-serif text-4xl md:text-6xl leading-tight text-[#F5F5F0]">
                    Vinci Executive
                    <span className="block bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] bg-clip-text text-transparent italic">
                      Wallet.
                    </span>
                  </h1>
                  <p className="mt-8 text-[#C9C9C2] text-sm md:text-base max-w-lg mx-auto leading-relaxed">
                    Unlimited settlement power. Machine-speed trading. Reserved for the
                    top <span className="text-[#F6E4A6]">2%</span> of Vinci counterparties.
                    Apply below — our Finance Desk personally reviews every application.
                  </p>
                  {/* PULSING GOLD CTA */}
                  <div className="mt-12 relative inline-block">
                    <span
                      aria-hidden
                      className="absolute inset-0 rounded-sm animate-ping"
                      style={{
                        background: "linear-gradient(90deg,#D8B23E,#F6E4A6,#B87333)",
                        opacity: 0.35,
                      }} />
                    <button
                      onClick={() => setShowInductionForm(true)}
                      data-testid="wallet-activate-cta"
                      className="relative px-10 py-5 bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk text-xs md:text-sm uppercase tracking-[0.4em] font-bold hover:shadow-[0_0_80px_rgba(246,228,166,0.7)] transition-all"
                      style={{
                        boxShadow: "0 0 60px rgba(216,178,62,0.6), 0 0 20px rgba(246,228,166,0.4)",
                      }}
                    >
                      <Sparkles className="inline-block w-4 h-4 mr-3 -mt-0.5" />
                      Activate Executive Wallet
                    </button>
                  </div>
                  <div className="mt-6 font-grotesk text-[10px] text-[#8A8A82] tracking-[0.28em] uppercase">
                    Approvals typically issued within 48 hours
                  </div>
                </div>
              </div>
              <div className="h-1 bg-gradient-to-r from-[#B87333] via-[#F6E4A6] to-[#D8B23E]" />
            </motion.div>
          )}

          {showInductionForm && inductionStatus === "not_applied" && (
            <WalletInductionForm
              onSubmitted={() => { setShowInductionForm(false); load(); }}
            />
          )}

          {(inductionStatus === "pending" || decisionAwaitingAck) && (
            <InductionPendingScreen
              application={application}
              onAcknowledge={decisionAwaitingAck ? acknowledgeDecision : undefined}
            />
          )}

          {inductionStatus === "rejected" && !decisionAwaitingAck && (
            <InductionRejectedScreen
              application={application}
              onReapply={() => { setShowInductionForm(true);
                                    setState((s) => ({ ...s,
                                                         balance: { ...(s.balance || {}), induction_status: "not_applied" } })); }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="wallet-page"
         data-tier={tier.key}
         className="relative min-h-screen text-[#F5F5F0] px-6 py-12 md:px-16 overflow-hidden"
         style={{
           background:
             `linear-gradient(180deg, ${tier.bgFrom} 0%, ${tier.bgVia} 45%, #050505 100%)`,
         }}>
      {pulse.modal}
      <BiometricPulseEntry tier={tier} onDone={() => setScanDone(true)} />
      {/* Defer the Security-Pulse OTP overlay until after the Biometric
          scan finishes so the LiquidBalance reveal isn't obscured. */}
      {scanDone && pulse.modal}
      <div className="pointer-events-none absolute inset-0 opacity-45"
           style={{
             background:
               `radial-gradient(1200px 700px at 20% -10%, ${tier.glow}, transparent 60%),`
               + `radial-gradient(900px 600px at 90% 10%, ${tier.ring}, transparent 60%),`
               + "radial-gradient(700px 500px at 50% 100%, rgba(47,185,153,0.06), transparent 60%)",
           }} />
      <div className="relative max-w-6xl mx-auto space-y-10">

        {/* ── Hero ── */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.7, delay: 0.3 }}
                     data-testid="wallet-hero"
                     className="rounded-sm p-8 relative overflow-hidden"
                     style={{
                       background:
                         `linear-gradient(135deg, rgba(255,255,255,0.03), rgba(0,0,0,0.5))`,
                       border: `1px solid ${tier.primary}44`,
                       backdropFilter: "blur(18px)",
                       boxShadow: `0 40px 120px -30px ${tier.glow}, inset 0 1px 0 rgba(255,255,255,0.05)`,
                     }}>
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-8 items-center">
            {/* Liquid Balance medallion */}
            <div className="flex justify-center lg:justify-start">
              <LiquidBalance balance={bal.balance || 0} tier={tier} size={260} />
            </div>

            {/* Meta */}
            <div className="text-center lg:text-left">
              <div
                className="font-grotesk text-[10px] uppercase tracking-[0.32em] inline-flex items-center gap-2"
                style={{ color: tier.accentSoft }}
              >
                <Wallet size={12} /> Vinci Executive Wallet · {tier.tagline}
              </div>
              <div
                data-testid="wallet-motto"
                className="font-serif italic mt-3 text-sm md:text-base"
                style={{ color: tier.accent, letterSpacing: "0.03em" }}
              >
                Veni · Vidi · Vici · Synchronized Intelligence
              </div>
              {bal.payment_restriction?.active && (
                <div data-testid="wallet-payment-restricted"
                      className="mt-4 flex items-start gap-3 border border-[#C0392B]/60 bg-[#C0392B]/10 rounded-sm px-4 py-3 text-left max-w-xl">
                  <ShieldCheck size={16} className="text-[#C0392B] mt-0.5 shrink-0" />
                  <div>
                    <div className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-[#C0392B]">Wallet payments restricted by Vinci Inc</div>
                    <div className="font-body text-sm text-[#F5F5F0] mt-1">{bal.payment_restriction.reason}</div>
                    <div className="font-body text-[11px] text-[#8A8A82] mt-1">Top-ups and statements remain available. Use Razorpay to settle invoices meanwhile.</div>
                  </div>
                </div>
              )}
              {!bal.can_trade && (
                <div data-testid="wallet-below-floor"
                      className="mt-4 inline-flex items-center gap-2 border border-[#C0392B]/50 bg-[#C0392B]/10 rounded-sm px-3 py-2 text-[11px] font-body text-[#C0392B]">
                  <ShieldCheck size={12} />
                  Minimum ecosystem balance of ₹{bal.ecosystem_floor || 500} required to trade — top up to unlock Request-a-Quote and Generate-PO.
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-4 justify-center lg:justify-start">
                <div className="text-[11px] font-body text-[#8A8A82]">
                  Credited lifetime: <b className="text-[#2FB999]">{inr(bal.total_credited || 0)}</b>
                </div>
                <div className="text-[11px] font-body text-[#8A8A82]">
                  Debited lifetime:{" "}
                  <b style={{ color: tier.primary }}>{inr(bal.total_debited || 0)}</b>
                </div>
              </div>
              <TierProgressBar balance={bal.balance || 0} tier={tier} />
            </div>

            {/* CTAs */}
            <div className="flex flex-col gap-3 w-full sm:w-auto">
              <Button data-testid="wallet-add-money" onClick={() => setRechargeOpen(true)}
                       className="text-black font-grotesk uppercase tracking-[0.22em] text-[11px] rounded-sm px-6 py-6"
                       style={{
                         background: `linear-gradient(90deg, ${tier.primary}, ${tier.accent}, ${tier.primary})`,
                         boxShadow: `0 0 40px ${tier.glow}`,
                       }}>
                <Plus size={14} className="mr-1.5" /> Add Money
              </Button>
              <Button data-testid="wallet-withdraw"
                       onClick={() => {
                         if (state.treasury?.status !== "activated") {
                           toast.info("Complete Treasury Authorization to enable withdrawals");
                           return;
                         }
                         if (!state.bank) { setBankOpen(true); return; }
                         if (!state.bank.verified) { setVerifyOpen(true); return; }
                         setWithdrawOpen(true);
                       }}
                       variant="outline"
                       className="font-grotesk uppercase tracking-[0.22em] text-[11px] rounded-sm"
                       style={{
                         borderColor: `${tier.primary}55`,
                         color: tier.primary,
                       }}>
                <Send size={12} className="mr-1.5" /> Request Withdrawal
              </Button>
            </div>
          </div>
        </motion.div>

        {/* ── Vinci Treasury Activation gate ─────────────────────
             Shown whenever the partner does not yet hold an
             `activated` Treasury authorization — this hard-locks
             bank mapping behind the Finance Desk induction ritual. */}
        {state.treasury?.status !== "activated" && (
          <TreasuryFlow
            api={api}
            treasury={state.treasury}
            tier={tier}
            onRefresh={load}
            onActivated={() => {
              // Vault synchronized → auto-open the bank form after the
              // handshake dialog has finished its exit animation.
              setTimeout(() => setBankOpen(true), 700);
            }}
          />
        )}

        {/* ── Network Power grid + Linked bank in a two-col row ── */}
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6 items-start">
          <WalletNetworkPulse statement={state.statement} tier={tier} />
          <div className="space-y-6">
            <LinkedBankStrip bank={state.bank}
                              locked={state.treasury?.status !== "activated"}
                              onEdit={() => {
                                if (state.treasury?.status !== "activated") {
                                  toast.info("Request Treasury Authorization to map or edit a bank account");
                                  return;
                                }
                                setBankOpen(true);
                              }}
                              onVerify={() => setVerifyOpen(true)} />
            {state.payouts.length > 0 && (
              <PayoutsStrip rows={state.payouts} />
            )}
          </div>
        </div>

        {/* ── Holographic Statement ── */}
        <div data-testid="wallet-statement" className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div
                className="font-grotesk text-[10px] uppercase tracking-[0.32em]"
                style={{ color: tier.accentSoft }}
              >
                Holographic Ledger
              </div>
              <h2 className="font-serif text-3xl mt-2">Wallet Statement</h2>
              <p className="text-[13px] text-[#8A8A82] font-body mt-1">
                Every credit and debit — HMAC-signed, tilt-hover, tamper-evident.
              </p>
            </div>
            <div className="flex gap-2">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/wallet/statement/pdf`}
                 target="_blank" rel="noreferrer"
                 data-testid="wallet-statement-download-pdf"
                 className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-black hover:brightness-110 px-3 py-2 rounded-sm inline-flex items-center gap-1.5"
                 style={{
                   background: `linear-gradient(90deg, ${tier.primary}, ${tier.accent}, ${tier.primary})`,
                   boxShadow: `0 0 30px -10px ${tier.primary}`,
                 }}>
                <Download size={11} /> Letterhead PDF
              </a>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/wallet/statement?limit=200`}
                 target="_blank" rel="noreferrer"
                 data-testid="wallet-statement-download"
                 className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-[#C9C9C2] hover:text-[#D8B23E] border border-white/20 hover:border-[#D8B23E] px-3 py-2 rounded-sm inline-flex items-center gap-1.5">
                <Download size={11} /> JSON
              </a>
            </div>
          </div>

          {state.statement.length === 0 ? (
            <div className="border border-white/10 rounded-sm p-10 text-center text-[#6f6f68] font-body italic">
              No transactions yet — top up your wallet to get started.
            </div>
          ) : (
            <div className="space-y-3">
              {state.statement.map((r) => (
                <HolographicLedgerRow key={r.tx_id} row={r} tier={tier} />
              ))}
            </div>
          )}
        </div>

        <div data-testid="wallet-disclaimer"
             className="text-[11px] font-body text-[#6f6f68] leading-relaxed border-t border-white/10 pt-6 inline-flex items-start gap-2">
          <ShieldCheck size={14} className="text-[#8A6A1A] mt-0.5 shrink-0" />
          <span>{state.disclaimer}</span>
        </div>

        {/* ── Vinci Core Banking deep-link tile ── */}
        {state.vcb_enabled && (
        <a href="/vcb"
            data-testid="wallet-vcb-tile"
            className="block rounded-sm p-5 md:p-6 mt-4 relative overflow-hidden group transition-shadow border border-[#D8B23E]/70 bg-[#0A0804] shadow-[0_0_0_1px_rgba(216,178,62,0.15),0_30px_80px_-30px_rgba(216,178,62,0.35)] hover:shadow-[0_0_40px_-10px_rgba(216,178,62,0.6)]">
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#D8B23E] text-black font-grotesk text-[8px] uppercase tracking-[0.3em] px-2 py-0.5" data-testid="wallet-vcb-premium-badge">♛ Premium</span>
          <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
                style={{ background:
                  `radial-gradient(500px 200px at 100% 0%, ${tier.primary}, transparent 70%)` }} />
          <div className="relative flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <Sparkles size={22} className="text-[#D8B23E] mt-1 shrink-0" />
              <div>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E]">
                  Vinci Core Banking · Flagship Institutional Banking Product
                </div>
                <div className="font-serif text-xl md:text-2xl text-[#F5F5F0] mt-0.5">
                  Vinci Core Banking
                </div>
                <div className="font-body text-xs text-[#C9C9C2] mt-1 max-w-xl">
                  Invitation-based · RBI-governed framework · 157 banks coverage ·
                  <span className="text-[#D8B23E]"> 0% handling</span> across UPI · IMPS · NEFT · RTGS.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <a href="/vcb-terminal"
                  data-testid="wallet-vcb-terminal-link"
                  onClick={(e) => e.stopPropagation()}
                  className="font-grotesk text-[10px] uppercase tracking-[0.28em] px-3 py-2 border border-[#D8B23E]/50 text-[#D8B23E] hover:bg-[#D8B23E] hover:text-black transition-colors">
                Sovereign Terminal
              </a>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E] group-hover:translate-x-1 transition-transform">
                Enter →
              </div>
            </div>
          </div>
        </a>
        )}
      </div>

      <RechargeModal open={rechargeOpen} setOpen={setRechargeOpen}
                       bal={bal} api={api} user={user}
                       onDone={load}
                       onCredited={(payload) => setSplash(payload)} />
      <WithdrawModal open={withdrawOpen} setOpen={setWithdrawOpen}
                       bal={bal} bank={state.bank} pulse={pulse} api={api}
                       onDone={load} />
      <LinkedBankModal open={bankOpen} setOpen={setBankOpen}
                          bank={state.bank} api={api} onDone={load}
                          onSaved={() => setVerifyOpen(true)} />
      <BankVerifyModal open={verifyOpen} setOpen={setVerifyOpen}
                          bank={state.bank} api={api} onDone={load} />
      <RechargeSuccessSplash
        open={!!splash}
        tier={tier}
        amount={splash?.amount || 0}
        balanceAfter={splash?.balance_after || 0}
        onClose={() => setSplash(null)}
      />
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Tier Progress Bar · shows how close the wallet is to the next tier
   ═════════════════════════════════════════════════════════════════ */
function TierProgressBar({ balance, tier }) {
  const b = Number(balance || 0);
  let pct, nextLabel, nextAt;
  if (tier.key === "bronze") {
    pct = Math.min(100, (b / 100000) * 100);
    nextLabel = "Silver"; nextAt = "₹1,00,000";
  } else if (tier.key === "silver") {
    pct = Math.min(100, ((b - 100000) / 900000) * 100);
    nextLabel = "Vinci Gold"; nextAt = "₹10,00,000";
  } else if (tier.key === "gold") {
    pct = Math.min(100, ((b - 1000000) / 1500000) * 100);
    nextLabel = "Vinci Platinum"; nextAt = "₹25,00,000";
  } else {
    pct = 100; nextLabel = null; nextAt = null;
  }
  return (
    <div className="mt-5" data-testid="wallet-tier-progress">
      <div className="flex items-baseline justify-between">
        <div
          className="font-grotesk text-[9px] uppercase tracking-[0.32em]"
          style={{ color: tier.accentSoft }}
        >
          {nextLabel ? `Progress · ${nextLabel} at ${nextAt}` : "Executive Cap Reached"}
        </div>
        <div
          className="font-mono text-[10px]"
          style={{ color: tier.accent }}
        >
          {Math.round(pct)}%
        </div>
      </div>
      <div
        className="mt-1.5 h-[3px] rounded-sm overflow-hidden"
        style={{ background: "rgba(255,255,255,0.06)" }}
      >
        <motion.div
          className="h-full"
          style={{
            background: `linear-gradient(90deg, ${tier.primary}, ${tier.accent})`,
            boxShadow: `0 0 12px ${tier.glow}`,
          }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Linked-bank strip · shows the beneficiary + edit action
   ═════════════════════════════════════════════════════════════════ */
function LinkedBankStrip({ bank, locked, onEdit, onVerify }) {
  if (!bank) {
    // When treasury is not yet activated, the TreasuryFlow gate above
    // already shows the CTA — this strip stays minimal so the page
    // doesn't feel duplicative.
    if (locked) {
      return (
        <div data-testid="wallet-linked-bank-locked"
              className="border border-white/10 rounded-sm p-5 flex items-center gap-3 bg-black/20 opacity-60">
          <Lock size={16} className="text-[#8A8A82]" />
          <div className="text-[13px] text-[#8A8A82] font-body italic">
            Bank mapping unlocks once Treasury Authorization is complete.
          </div>
        </div>
      );
    }
    return (
      <div data-testid="wallet-linked-bank-empty"
            className="border border-dashed border-[#8A6A1A]/40 rounded-sm p-5 flex items-center justify-between gap-4 bg-[#0a0a0a]">
        <div className="flex items-center gap-3">
          <Landmark size={18} className="text-[#8A6A1A]" />
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">
              No bank account linked
            </div>
            <div className="text-[13px] text-[#C9C9C2] font-body">
              Link a bank account to enable withdrawals from your wallet.
            </div>
          </div>
        </div>
        <Button data-testid="wallet-link-bank"
                 onClick={onEdit}
                 className="bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk uppercase tracking-[0.22em] text-[10px]">
          <Plus size={11} className="mr-1" /> Link Bank
        </Button>
      </div>
    );
  }
  const verified = bank.verified;
  const border = verified ? "border-[#2FB999]/30 bg-[#02150F]"
                             : "border-[#D8B23E]/40 bg-[#1B1401]/40";
  return (
    <div data-testid="wallet-linked-bank"
          className={`border rounded-sm p-5 flex items-center justify-between gap-4 flex-wrap ${border}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full grid place-items-center ${verified ? "bg-[#2FB999]/15" : "bg-[#D8B23E]/15"}`}>
          {verified
            ? <Landmark size={16} className="text-[#2FB999]" />
            : <AlertTriangle size={16} className="text-[#D8B23E]" />}
        </div>
        <div>
          <div className={`font-grotesk text-[10px] uppercase tracking-[0.22em] inline-flex items-center gap-1.5 ${verified ? "text-[#2FB999]" : "text-[#D8B23E]"}`}
                data-testid={verified ? "wallet-bank-verified-badge" : "wallet-bank-unverified-badge"}>
            {verified
              ? <><BadgeCheck size={11} /> Verified Payout Account</>
              : <><AlertTriangle size={11} /> Bank Verification Pending</>}
          </div>
          <div className="text-[14px] text-[#F5F5F0] font-body mt-0.5">
            {bank.holder_name} · {bank.bank_name || "Bank"} · IFSC {bank.ifsc}
          </div>
          <div className="text-[11px] font-mono text-[#8A8A82]">
            AC **** **** {(bank.account_number || "").slice(-4)}
            {verified && bank.verified_at && (
              <span className="ml-2 font-body italic">
                · verified {new Date(bank.verified_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!verified && (
          <Button data-testid="wallet-verify-bank" onClick={onVerify}
                   className="bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk uppercase tracking-[0.22em] text-[10px]">
            <KeyRound size={11} className="mr-1" /> Verify Bank
          </Button>
        )}
        <button data-testid="wallet-edit-bank" onClick={onEdit}
                 className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#D8B23E] border border-white/10 hover:border-[#D8B23E] px-3 py-1.5 rounded-sm">
          Edit
        </button>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Bank Verify Modal · penny-drop micro-deposit verification
   ═════════════════════════════════════════════════════════════════ */
function BankVerifyModal({ open, setOpen, bank, api, onDone }) {
  const [stage, setStage] = useState("intro");   // intro | dispatched | confirming
  const [handle, setHandle] = useState(null);
  const [amount, setAmount] = useState("");
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStage("intro"); setHandle(null); setAmount(""); setAttemptsLeft(3);
    // Pull any existing pending verification so a returning user can
    // enter the amount directly.
    api.get("/wallet/payout/verify-bank/status")
      .then(({ data }) => {
        const lv = data.latest_verification;
        if (lv && lv.status === "pending") {
          setStage("dispatched");
          setHandle({ verification_id: lv.id, expires_at: lv.expires_at });
          setAttemptsLeft(Math.max(0, 3 - (lv.attempts || 0)));
        }
      })
      .catch(() => {});
  }, [open, api]);

  const startDrop = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/payout/verify-bank/start", {});
      if (data.already_verified) {
        toast.success("Bank already verified");
        setOpen(false); await onDone?.();
        return;
      }
      setHandle(data);
      setAttemptsLeft(data.attempts_left ?? 3);
      setStage("dispatched");
      toast.success(
        data.reused_existing
          ? "Existing verification retrieved — check your bank for the deposit"
          : "Penny-drop dispatched — check your bank statement in 15-30 minutes",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start verification");
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/wallet/payout/verify-bank/confirm",
        { amount: Number(amount) });
      toast.success("Bank verified · withdrawals unlocked");
      setOpen(false); await onDone?.();
    } catch (e) {
      const detail = e.response?.data?.detail || "Verification failed";
      toast.error(detail);
      // Track attempts left from server hint
      const m = detail.match(/(\d+) attempt/);
      if (m) setAttemptsLeft(Math.max(0, Number(m[1])));
      if (detail.toLowerCase().includes("re-link")) {
        setOpen(false); await onDone?.();
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="wallet-verify-modal"
                       className="bg-[#0A0906] border border-[#D8B23E]/40 text-[#F5F5F0] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
            <KeyRound className="text-[#D8B23E]" size={18} /> Verify Payout Bank
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[#C9C9C2] pt-2">
            Vinci dispatches a tiny <b className="text-[#D8B23E]">₹1.01–₹1.99</b> penny drop
            to your linked bank via IMPS/NEFT. Enter the exact amount you
            receive — matching amount unlocks withdrawals for this
            account.
          </DialogDescription>
        </DialogHeader>

        {bank && (
          <div className="border border-white/10 bg-[#0F0C05] rounded-sm p-3 flex items-center gap-3">
            <Landmark size={14} className="text-[#8A6A1A]" />
            <div className="text-[12px] font-body text-[#C9C9C2]">
              <b className="text-[#F5F5F0]">{bank.holder_name}</b> ·{" "}
              {bank.bank_name} · IFSC {bank.ifsc} · AC ****{(bank.account_number || "").slice(-4)}
            </div>
          </div>
        )}

        {stage === "intro" && (
          <div className="space-y-3">
            <ol className="text-[13px] text-[#C9C9C2] font-body leading-relaxed space-y-2 border-l-2 border-[#D8B23E]/30 pl-4">
              <li><b className="text-[#D8B23E]">1.</b> Click <i>Dispatch penny drop</i> below.</li>
              <li><b className="text-[#D8B23E]">2.</b> The Vinci treasury officer will send ₹1.01–₹1.99 to your bank within 24 hours.</li>
              <li><b className="text-[#D8B23E]">3.</b> Check your bank statement for the exact amount.</li>
              <li><b className="text-[#D8B23E]">4.</b> Return here and enter the amount — matching amount = verified. 3 tries.</li>
            </ol>
          </div>
        )}

        {stage === "dispatched" && (
          <div className="space-y-3">
            <div className="border border-[#2FB999]/30 bg-[#02150F] rounded-sm p-4 text-center">
              <CheckCircle2 size={24} className="text-[#2FB999] inline-block" />
              <div className="font-serif text-lg text-[#F5F5F0] mt-2">Penny-drop dispatched</div>
              <div className="text-[12px] text-[#C9C9C2] font-body mt-1">
                Expected in your bank within 24 hours. Return here once you see the credit.
              </div>
              {handle?.expires_at && (
                <div className="text-[10px] text-[#8A8A82] mt-2">
                  Expires · {new Date(handle.expires_at).toLocaleString()}
                </div>
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">
                Amount received (₹) · {attemptsLeft} attempt{attemptsLeft !== 1 ? "s" : ""} left
              </Label>
              <Input data-testid="wallet-verify-amount"
                      type="number" step="0.01" min="0.01" max="99"
                      value={amount} onChange={(e) => setAmount(e.target.value)}
                      placeholder="e.g. 1.47"
                      className="bg-black/40 border-white/20 mt-1 text-lg font-mono" />
              <div className="text-[10px] text-[#6f6f68] mt-1 font-body">
                Enter the exact amount to two decimal places.
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}
                   className="border-white/20 text-[#C9C9C2]">Close</Button>
          {stage === "intro" && (
            <Button data-testid="wallet-verify-dispatch" onClick={startDrop} disabled={busy}
                     className="bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk uppercase tracking-[0.22em] text-[11px]">
              {busy ? <><Loader2 className="animate-spin mr-1.5" size={12} /> Dispatching…</>
                     : <><KeyRound size={12} className="mr-1.5" /> Dispatch Penny Drop</>}
            </Button>
          )}
          {stage === "dispatched" && (
            <Button data-testid="wallet-verify-confirm" onClick={confirm}
                     disabled={busy || !amount || Number(amount) <= 0 || attemptsLeft <= 0}
                     className="bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk uppercase tracking-[0.22em] text-[11px]">
              {busy ? <><Loader2 className="animate-spin mr-1.5" size={12} /> Verifying…</>
                     : <><BadgeCheck size={12} className="mr-1.5" /> Verify Amount</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Payouts strip · shows recent withdrawal history
   ═════════════════════════════════════════════════════════════════ */
function PayoutsStrip({ rows }) {
  const icon = (s) => s === "dispatched"
    ? <CheckCircle2 size={13} className="text-[#2FB999]" />
    : s === "rejected"
      ? <XCircle size={13} className="text-[#C0392B]" />
      : <Clock size={13} className="text-[#D8B23E]" />;
  const label = (s) => s === "dispatched" ? "Dispatched" :
                        s === "rejected" ? "Declined" : "Pending Review";
  const colour = (s) => s === "dispatched" ? "#2FB999" :
                          s === "rejected" ? "#C0392B" : "#D8B23E";
  return (
    <div data-testid="wallet-payouts-strip" className="space-y-3">
      <div>
        <div className="overline text-[#B87333]">Payouts</div>
        <h3 className="font-serif text-2xl mt-1">Withdrawal Requests</h3>
      </div>
      <div className="border border-white/10 rounded-sm overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-white/[0.03] text-[#8A8A82]">
            <tr>
              <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Requested</th>
              <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Purpose</th>
              <th className="text-right px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Amount</th>
              <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Status</th>
              <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">UTR / Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`wallet-payout-${r.id}`}
                  className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="px-4 py-3 text-[#8A8A82] text-[11px]">
                  {new Date(r.requested_at).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-[#C9C9C2] font-body italic">"{r.purpose}"</td>
                <td className="px-4 py-3 text-right font-mono text-[#F5F5F0]">
                  {inr(r.amount)}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-grotesk uppercase tracking-[0.18em]"
                        style={{ color: colour(r.status) }}>
                    {icon(r.status)} {label(r.status)}
                  </span>
                </td>
                <td className="px-4 py-3 text-[11px] font-mono text-[#8A8A82]">
                  {r.utr_reference || r.admin_note || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Recharge Modal · Razorpay Checkout flow
   ═════════════════════════════════════════════════════════════════ */
function RechargeModal({ open, setOpen, bal, api, user, onDone, onCredited }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const pollTimer = useRef(null);

  const minR = bal.min_recharge || 500;
  const maxR = bal.max_recharge_per_tx || 800000;

  const loadRzpScript = () => new Promise((res, rej) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true); s.onerror = () => rej(new Error("Razorpay SDK load failed"));
    document.body.appendChild(s);
  });

  // Fallback poll — if /verify fails or times out (e.g. flaky signature)
  // we still poll /wallet/balance every 3s for 30s so a webhook-delivered
  // credit produces the same splash without a manual refresh.
  const startBalancePoll = (amt, startingBalance) => {
    setPolling(true);
    let elapsed = 0;
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = setInterval(async () => {
      elapsed += 3;
      try {
        const { data } = await api.get("/wallet/balance");
        if ((data.balance || 0) > startingBalance) {
          clearInterval(pollTimer.current);
          pollTimer.current = null;
          setPolling(false); setBusy(false);
          setOpen(false); setAmount("");
          onCredited && onCredited({
            amount: (data.balance || 0) - startingBalance,
            balance_after: data.balance || 0,
          });
          onDone && onDone();
          return;
        }
      } catch { /* silent — keep polling */ }
      if (elapsed >= 30) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
        setPolling(false); setBusy(false);
        toast.error("Recharge still processing — please refresh in a minute.");
      }
    }, 3000);
  };

  useEffect(() => () => {
    if (pollTimer.current) clearInterval(pollTimer.current);
  }, []);

  const run = async (railChoice) => {
    const amt = Number(amount);
    if (!(amt >= minR && amt <= maxR)) {
      toast.error(`Enter an amount between ${inr(minR)} and ${inr(maxR)}`);
      return;
    }
    setBusy(railChoice);
    const startingBalance = bal.balance || 0;
    try {
      const { data: init } = await api.post(`/wallet/recharge/init?rail=${railChoice}`, { amount: amt });
      if (redirectIfPhonePe(init)) return;
      await loadRzpScript();
      const rzp = new window.Razorpay({
        key: init.key_id,
        amount: Math.round(amt * 100),
        currency: init.currency, name: "Vinci Executive Wallet",
        description: `Wallet top-up · ${inr(amt)}`,
        order_id: init.order_id,
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#D8B23E" },
        handler: async (resp) => {
          try {
            const { data } = await api.post("/wallet/recharge/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setOpen(false); setAmount("");
            onCredited && onCredited({
              amount: amt,
              balance_after: data.balance_after,
            });
            onDone && onDone();
            setBusy(false);
          } catch (e) {
            // Verify failed → maybe webhook will credit. Poll balance.
            toast.info("Verification pending — awaiting bank confirmation…");
            startBalancePoll(amt, startingBalance);
          }
        },
        modal: {
          ondismiss: () => { setBusy(false); toast.info("Recharge cancelled"); },
        },
      });
      rzp.open();
    } catch (e) {
      setBusy(false);
      toast.error(e.response?.data?.detail || "Could not start recharge");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="wallet-recharge-modal"
                       className="bg-[#0A0906] border border-[#D8B23E]/40 text-[#F5F5F0]">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
            <Plus className="text-[#D8B23E]" size={18} /> Add Money to Vinci Wallet
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[#C9C9C2] font-body pt-2">
            Fund your wallet via Razorpay or PhonePe — your choice. Every top-up is
            HMAC-signed and reflected instantly.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label className="text-[11px] uppercase tracking-[0.22em] font-grotesk text-[#8A6A1A]">
            Amount (INR)
          </Label>
          <Input data-testid="wallet-recharge-amount" type="number"
                  min={minR} max={maxR} step="100"
                  value={amount} onChange={(e) => setAmount(e.target.value)}
                  placeholder={`e.g. ${(minR * 20).toLocaleString("en-IN")}`}
                  className="bg-black/40 border-white/20 text-lg font-mono" />
          <div className="text-[11px] text-[#8A8A82] font-body">
            Min <b className="text-[#F5F5F0]">{inr(minR)}</b> · Max per transaction{" "}
            <b className="text-[#F5F5F0]">{inr(maxR)}</b>.
          </div>
          {polling
            ? <div data-testid="wallet-recharge-polling" className="inline-flex items-center gap-2 text-[11px] text-[#D8B23E] font-grotesk uppercase tracking-[0.2em]"><Loader2 className="animate-spin" size={13} /> Awaiting bank…</div>
            : <PayRailButtons testId="wallet-recharge-submit" onPay={run} busy={busy} disabled={!amount} amountLabel={amount ? inr(Number(amount)) : ""} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={!!busy}
                   className="border-white/20 text-[#C9C9C2]">Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Withdraw Modal · uses the LINKED bank; enforces ₹500 floor
   ═════════════════════════════════════════════════════════════════ */
function WithdrawModal({ open, setOpen, bal, bank, pulse, api, onDone }) {
  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("");
  const [busy, setBusy] = useState(false);

  const floor = bal.ecosystem_floor || 500;
  const maxWith = Math.max(0, (bal.balance || 0) - floor);

  const run = async () => {
    setBusy(true);
    try {
      const tok = await pulse.request("wallet_payout_request", null);
      if (!tok) { setBusy(false); return; }
      const { data } = await api.post("/wallet/payout/request",
        { amount: Number(amount), purpose: purpose.trim() },
        { headers: { "X-Security-Pulse": tok } },
      );
      toast.success(`Withdrawal request submitted · ${data.request_id.slice(0, 8)} · admin will review`);
      setOpen(false); setAmount(""); setPurpose(""); onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Withdrawal failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="wallet-withdraw-modal"
                       className="bg-[#0A0906] border border-[#B87333]/40 text-[#F5F5F0] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
            <Send className="text-[#B87333]" size={18} /> Request Withdrawal
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[#C9C9C2] font-body pt-2">
            Withdraw wallet balance to your linked bank. Admin approves
            every request before the treasury dispatches funds — typical
            SLA <b className="text-[#D8B23E]">24 hours</b>.
          </DialogDescription>
        </DialogHeader>
        {bank && (
          <div className="border border-[#2FB999]/30 bg-[#02150F] rounded-sm p-3 flex items-center gap-3">
            <Landmark size={16} className="text-[#2FB999]" />
            <div className="text-[12px] font-body text-[#C9C9C2]">
              Dispatching to <b className="text-[#F5F5F0]">{bank.holder_name}</b> ·{" "}
              <span className="text-[#8A8A82]">{bank.bank_name} · IFSC {bank.ifsc} · AC ****{(bank.account_number || "").slice(-4)}</span>
            </div>
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">
              Amount · max {inr(maxWith)} (floor {inr(floor)})
            </Label>
            <Input data-testid="wallet-withdraw-amount" type="number"
                    min={100} max={maxWith}
                    value={amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder="Amount to withdraw"
                    className="bg-black/40 border-white/20 mt-1 text-lg font-mono" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">Purpose</Label>
            <Input data-testid="wallet-withdraw-purpose"
                    value={purpose} onChange={(e) => setPurpose(e.target.value)}
                    placeholder="e.g. Move Q1 commission to my current account"
                    className="bg-black/40 border-white/20 mt-1" />
          </div>
        </div>
        <div className="text-[10px] text-[#6f6f68] font-body italic pt-2 border-t border-white/10">
          Vinci Wallet is closed-loop. Funds move only after admin
          approval. Approving debits your wallet and moves the amount to
          your linked bank via IMPS/NEFT.
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}
                   className="border-white/20 text-[#C9C9C2]">Cancel</Button>
          <Button data-testid="wallet-withdraw-submit" onClick={run}
                   disabled={busy || !amount || !purpose || purpose.length < 4}
                   className="bg-[#B87333] hover:bg-[#A05F26] text-black font-grotesk uppercase tracking-[0.22em] text-[11px]">
            {busy ? "Submitting…" : "Submit Request (OTP required)"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Linked Bank Modal · save / update the payout account
   ═════════════════════════════════════════════════════════════════ */
function LinkedBankModal({ open, setOpen, bank, api, onDone, onSaved }) {
  const [form, setForm] = useState({ holder_name: "", bank_name: "",
                                        ifsc: "", account_number: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({
        holder_name: bank?.holder_name || "",
        bank_name: bank?.bank_name || "",
        ifsc: bank?.ifsc || "",
        account_number: bank?.account_number || "",
      });
    }
  }, [open, bank]);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const run = async () => {
    setBusy(true);
    try {
      await api.post("/wallet/payout/linked-bank", form);
      toast.success("Payout bank linked");
      setOpen(false);
      await onDone?.();
      // Chain: user clicked "Withdraw" but had no bank → open withdraw next
      setTimeout(() => onSaved && onSaved(), 200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save bank");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="wallet-linked-bank-modal"
                       className="bg-[#0A0906] border border-[#D8B23E]/40 text-[#F5F5F0]">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
            <Landmark className="text-[#D8B23E]" size={18} />
            {bank ? "Update linked bank" : "Link payout bank"}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[#C9C9C2] font-body pt-2">
            Your withdrawals will be sent to this bank account. Vinci
            partners hold one linked account at a time.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">Account holder name</Label>
            <Input data-testid="wallet-bank-holder" value={form.holder_name}
                    onChange={(e) => upd("holder_name", e.target.value)}
                    className="bg-black/40 border-white/20 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">Bank name</Label>
            <Input data-testid="wallet-bank-name" value={form.bank_name}
                    onChange={(e) => upd("bank_name", e.target.value)}
                    placeholder="e.g. HDFC Bank"
                    className="bg-black/40 border-white/20 mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">IFSC</Label>
              <Input data-testid="wallet-bank-ifsc" value={form.ifsc}
                      onChange={(e) => upd("ifsc", e.target.value.toUpperCase())}
                      placeholder="HDFC0001234"
                      className="bg-black/40 border-white/20 mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">Account number</Label>
              <Input data-testid="wallet-bank-account" value={form.account_number}
                      onChange={(e) => upd("account_number", e.target.value)}
                      className="bg-black/40 border-white/20 mt-1 font-mono" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}
                   className="border-white/20 text-[#C9C9C2]">Cancel</Button>
          <Button data-testid="wallet-bank-save" onClick={run}
                   disabled={busy || !form.holder_name || !form.bank_name
                              || form.ifsc.length < 6 || form.account_number.length < 6}
                   className="bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk uppercase tracking-[0.22em] text-[11px]">
            {busy ? <><Loader2 className="animate-spin mr-1.5" size={12} /> Saving…</>
                   : <><Save size={12} className="mr-1.5" /> Save Bank</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
